import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Typography } from 'app/components';
import { useAddTaxonomyToConfig, useRemoveTaxonomyFromConfig, DocumentTaxonomyConfigTaxonomy, useMandatoryProgress } from 'entities/tag';
import { TagAction, TagItemShortcuts } from '../../../TagItemShortcuts/TagItemShortcuts';
import { formatTaxonomyName } from 'app/utils/tag-utils';
import { TaxonomyHeader } from '../TaxonomyHeader/TaxonomyHeader';
import { AddTaxonomyModal, RemoveModal } from '../modals';

import './ActiveTaxonomiesList.scss';
import Tippy from '@tippyjs/react';

interface ActiveTaxonomiesListProps {
  configId: string;
  documentSuperId: number;
  taxonomySelections: DocumentTaxonomyConfigTaxonomy[];
}

const MandatoryProgressLine = ({ documentSuperId, taxonomyId }: { documentSuperId: number; taxonomyId: string }) => {
  const { t } = useTranslation(['all', 'tag']);

  const { data } = useMandatoryProgress(documentSuperId, taxonomyId);
  const addressed = data?.addressed ?? 0;
  const total = data?.total ?? 0;
  return <Typography variant="body-7" fontWeight="light">{`${addressed} ${t('tag:of.label')} ${total} ${t('all:tags.tags')}`}</Typography>;
};

export const ActiveTaxonomiesList = ({ configId, documentSuperId, taxonomySelections }: ActiveTaxonomiesListProps) => {
  const { t } = useTranslation(['all', 'tag', 'taxonomy']);

  const { mutate: addTaxonomyToConfig } = useAddTaxonomyToConfig();
  const { mutate: removeTaxonomyFromConfig, isPending: isRemoving } = useRemoveTaxonomyFromConfig();

  const [defaultTaxonomy, setDefaultTaxonomy] = useState<string>('');
  const [showTaxonomyModal, setShowTaxonomyModal] = useState(false);
  const [removeModalState, setRemoveModalState] = useState({ show: false, taxId: '', taxonomyName: '', tagCount: 0 });

  useEffect(() => {
    if (taxonomySelections.length > 0 && !defaultTaxonomy) {
      setDefaultTaxonomy(taxonomySelections[0].taxonomyId);
    }
  }, [taxonomySelections, defaultTaxonomy]);

  const handleAddTaxonomy = (taxonomyId: string) => {
    addTaxonomyToConfig({ configId, taxonomyId });
  };

  const handleRemoveTaxonomy = (taxonomyId: string) => {
    const tax = taxonomySelections.find((t) => t.taxonomyId === taxonomyId);
    setRemoveModalState({
      show: true,
      taxId: taxonomyId,
      taxonomyName: tax?.taxonomy?.name ? formatTaxonomyName(tax.taxonomy.name) : '',
      tagCount: tax?.usedTags ?? 0,
    });
  };

  const onConfirmRemove = () => {
    removeTaxonomyFromConfig(
      { configId, taxonomyId: removeModalState.taxId },
      { onSettled: () => setRemoveModalState({ show: false, taxId: '', taxonomyName: '', tagCount: 0 }) }
    );
  };

  return (
    <>
      <TaxonomyHeader onLoadTaxonomies={() => setShowTaxonomyModal(true)} />

      <section className="taxonomy__active-taxonomies">
        {taxonomySelections.map((taxonomy) => {
          const actions: TagAction[] = [{ type: 'delete', onClick: () => handleRemoveTaxonomy(taxonomy.taxonomyId), label: t('taxonomy:delete') }];

          if (taxonomy.taxonomyId !== defaultTaxonomy) {
            actions.push({ type: 'default', onClick: () => setDefaultTaxonomy(taxonomy.taxonomyId) });
          }

          const usedTags = taxonomy.usedTags ?? 0;

          return (
            <div key={taxonomy.taxonomyId}>
              <div className="taxonomy__taxonomy-header">
                <div className="taxonomy__taxonomy-title">
                  <Typography variant="body-8" fontWeight="semibold">
                    {taxonomy.taxonomy?.name ? formatTaxonomyName(taxonomy.taxonomy.name) : ''}
                  </Typography>

                  {taxonomy.taxonomyId === defaultTaxonomy && (
                    <Tippy
                      content={
                        <Typography variant="body-5" color="#6c757d" className="defaults-section__tippy-content">
                          {t('tag:primary.taxonomy')}
                        </Typography>
                      }
                    >
                      <i className="fa-solid fa-xs fa-star mx-1" />
                    </Tippy>
                  )}
                </div>

                <TagItemShortcuts actions={actions} />
              </div>

              <div className="taxonomy__taxonomy-stats">
                <div className="taxonomy__taxonomy-content">
                  <Typography variant="body-5" fontWeight="bold" color="#547BA2" className="caps">
                    {t('tag:content.label')}
                  </Typography>

                  <Typography variant="body-7" fontWeight="light">{`${usedTags} ${t('tag:active.tags')}`}</Typography>
                </div>
                <div className="taxonomy__taxonomy-content">
                  <Typography variant="body-5" fontWeight="bold" color="#547BA2" className="caps">
                    {`${t('tag:mandatory.label')} ${t('all:tags.tags')}`}
                  </Typography>

                  <MandatoryProgressLine documentSuperId={documentSuperId} taxonomyId={taxonomy.taxonomyId} />
                </div>
              </div>
            </div>
          );
        })}
      </section>

      <AddTaxonomyModal
        show={showTaxonomyModal}
        connectedTaxonomies={taxonomySelections}
        onClose={() => setShowTaxonomyModal(false)}
        onAdd={handleAddTaxonomy}
      />

      <RemoveModal
        show={removeModalState.show}
        taxonomyName={removeModalState.taxonomyName}
        tagCount={removeModalState.tagCount}
        isLoading={isRemoving}
        onClose={() => setRemoveModalState({ show: false, taxId: '', taxonomyName: '', tagCount: 0 })}
        onConfirm={onConfirmRemove}
      />
    </>
  );
};
